import { useQuery } from "@tanstack/react-query";
import type { DateRange } from "@/hooks/useDateRange";
import { analyticsOperationsService } from "@/services/analytics-operations-service";
import { analyticsOperationsKeys } from "@/components/pages/analytics/front-desk/hooks/useOperationsAnalytics";
import { useAnalyticsOverviewRange } from "@/components/pages/analytics/front-desk/hooks/useAnalyticsOverviewRange";

/**
 * Focused stats for the Requests summary block (list view). Fetches only the
 * three operations metrics the block renders — total requests, canceled
 * requests, and average processing/response time — from /analytics/operations.
 */
export const useRequestOperationsStats = (
  dateRange: DateRange,
  enabled = true,
) => {
  const { hotelId, from, to, isReady } = useAnalyticsOverviewRange(dateRange);
  const queryEnabled = enabled && isReady;

  const totalRequests = useQuery({
    queryKey: analyticsOperationsKeys.totalRequests(hotelId, from, to),
    queryFn: () =>
      analyticsOperationsService.getTotalRequests({ hotelId, from, to }),
    enabled: queryEnabled,
    staleTime: 5 * 60 * 1000,
  });

  const onHoldCanceled = useQuery({
    queryKey: analyticsOperationsKeys.onHoldCanceled(hotelId, from, to),
    queryFn: () =>
      analyticsOperationsService.getOnHoldCanceled({ hotelId, from, to }),
    enabled: queryEnabled,
    staleTime: 5 * 60 * 1000,
  });

  const processingTime = useQuery({
    queryKey: analyticsOperationsKeys.processingTime(hotelId, from, to),
    queryFn: () =>
      analyticsOperationsService.getProcessingTime({ hotelId, from, to }),
    enabled: queryEnabled,
    staleTime: 5 * 60 * 1000,
  });

  return {
    totalRequests: totalRequests.data?.totals.total ?? null,
    canceledRequests: onHoldCanceled.data?.totals.canceled ?? null,
    avgResponseTime: processingTime.data?.totals.humanReadable ?? null,
    isLoading:
      totalRequests.isLoading ||
      onHoldCanceled.isLoading ||
      processingTime.isLoading,
    isError:
      totalRequests.isError || onHoldCanceled.isError || processingTime.isError,
  };
};
